import { Subscription } from 'rxjs';
import { PluginContext } from '../../mol-plugin/context';
import { WebMcpActor } from './provenance';

export type WebMcpActivityKind = 'selection' | 'focus' | 'hierarchy' | 'measurement' | 'camera';

export interface WebMcpActivityEntry {
    readonly id: string
    readonly at: number
    readonly actor: WebMcpActor
    readonly kind: WebMcpActivityKind
    readonly summary: string
}

/** Compact viewer activity log. Agent mutations suppress their duplicate semantic events. */
export class WebMcpActivityLog {
    private readonly entries: WebMcpActivityEntry[] = [];
    private nextId = 1;
    private agentMutationDepth = 0;

    add(actor: WebMcpActor, kind: WebMcpActivityKind, summary: string, at = Date.now()): WebMcpActivityEntry | undefined {
        if (this.agentMutationDepth > 0) return;
        const entry: WebMcpActivityEntry = { id: `activity-${this.nextId++}`, at, actor, kind, summary };
        this.entries.unshift(entry);
        return entry;
    }

    list(limit = 20): readonly WebMcpActivityEntry[] {
        return this.entries.slice(0, Math.max(0, limit));
    }

    beginAgentMutation(): () => void {
        this.agentMutationDepth++;
        return () => { this.agentMutationDepth--; };
    }
}

/** Subscribes only to state events Mol* exposes semantically. */
export function subscribeWebMcpActivity(plugin: PluginContext, activity: WebMcpActivityLog, cameraThrottleMs = 600): () => void {
    const subscriptions: Subscription[] = [];
    let firstFocus = true;
    let firstHierarchy = true;
    let firstCamera = true;
    let lastCameraAt = 0;

    subscriptions.push(plugin.managers.structure.selection.events.changed.subscribe(() => {
        activity.add('user', 'selection', 'Selection changed.');
    }));
    subscriptions.push(plugin.managers.structure.focus.behaviors.current.subscribe(focus => {
        if (firstFocus) { firstFocus = false; return; }
        activity.add('user', 'focus', focus ? 'Structure focus changed.' : 'Structure focus cleared.');
    }));
    subscriptions.push(plugin.managers.structure.hierarchy.behaviors.selection.subscribe(selection => {
        if (firstHierarchy) { firstHierarchy = false; return; }
        activity.add('user', 'hierarchy', `${selection.structures.length} structure${selection.structures.length === 1 ? '' : 's'} in hierarchy.`);
    }));
    subscriptions.push(plugin.managers.structure.measurement.behaviors.state.subscribe(() => {
        activity.add('user', 'measurement', 'Measurements changed.');
    }));
    if (plugin.canvas3d) subscriptions.push(plugin.canvas3d.camera.stateChanged.subscribe(() => {
        if (firstCamera) { firstCamera = false; return; }
        const now = Date.now();
        if (now - lastCameraAt < cameraThrottleMs) return;
        lastCameraAt = now;
        activity.add('user', 'camera', 'Camera updated.');
    }));

    return () => { for (const subscription of subscriptions) subscription.unsubscribe(); };
}
